/**
 * `/settings/checklist` — **Setup checklist** (C11, FR-A8 §2; spec C3; T-1.20).
 *
 * The way back from *Skip setup*. The hub draws this card only when the firm
 * has marked or skipped rows (as the single page did), but the page answers
 * whenever it is opened — a link kept from before still lands somewhere that
 * says what is true.
 *
 * *Show setup checklist* clears every mark, so the Overview's list is computed
 * from the data alone again. The data still outranks any mark either way.
 */

import { Alert, Button, EmptyState, Skeleton } from '@fiveox/ui'
import { ListChecks } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'

import { SETTINGS, useEvaluation } from '../../../lib/access'
import { ApiError } from '../../../lib/api'
import { firm } from '../../../lib/api/firm'
import type { ChecklistMark } from '../../../lib/api/site'
import { SettingsPage, SettingsSection } from './SettingsPage'

const ROW_LABELS: Record<string, string> = {
  profession: 'Your profession and firm details',
  template: 'Pick a template',
  content: 'Write your pages',
  services: 'List your services and their fees',
  domain: 'Bind your domain',
  publish: 'Publish your site',
  channels: 'Turn on email and messaging',
  client: 'Add your first client',
  case: 'Open your first case',
}

export function ChecklistSettings() {
  const { can } = useEvaluation()
  const editable = can(SETTINGS, 'can_update')
  const [marks, setMarks] = useState<ChecklistMark[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [restored, setRestored] = useState(false)

  useEffect(() => {
    firm
      .checklistMarks()
      .then((body) => setMarks(body.data.marks))
      .catch(() => setError('The checklist could not be read.'))
  }, [])

  const showAgain = async () => {
    setBusy(true)
    setError(null)
    try {
      setMarks((await firm.skipChecklist(null)).data.marks)
      setRestored(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The checklist could not be restored.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <SettingsPage
      title="Setup checklist"
      description="The getting-started list on your Overview, and the rows you marked done or skipped."
    >
      {error && <Alert tone="error">{error}</Alert>}
      {restored && (
        <Alert tone="success">
          The checklist is back on your <Link to="/" className="font-medium underline">Overview</Link>
          , worked out from your data alone.
        </Alert>
      )}
      {marks === null && !error ? (
        <Skeleton className="h-40 w-full rounded-2xl" />
      ) : marks && marks.length === 0 ? (
        <EmptyState
          title="Nothing marked or skipped"
          description="Every row on your Overview’s checklist is ticked by your data alone. Rows you mark done or skip there will be listed here."
        />
      ) : (
        marks && (
          <SettingsSection
            icon={<ListChecks size={18} aria-hidden />}
            title={`${marks.length} of 9 rows marked`}
            description="Showing the checklist again clears every mark below."
            footer={
              editable ? (
                <div className="flex justify-end">
                  <Button variant="secondary" onClick={() => void showAgain()} loading={busy}>
                    Show setup checklist
                  </Button>
                </div>
              ) : undefined
            }
          >
            <ul className="divide-border divide-y">
              {marks.map((mark) => (
                <li key={mark.row_key} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <span className="text-foreground">{ROW_LABELS[mark.row_key] ?? mark.row_key}</span>
                  <span className="text-muted-foreground">
                    {mark.state === 'skipped' ? 'Skipped' : 'Marked done'}
                    {mark.marked_at ? ` · ${new Date(mark.marked_at).toLocaleDateString()}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </SettingsSection>
        )
      )}
    </SettingsPage>
  )
}
